/**
 * Transport seam between BackendClient and a backend. Gameplay code never sees transports; switching
 * between the in-process backend, HTTP and WebSocket is a configuration change (see ../config.ts).
 */
import type { EventEnvelope, RequestEnvelope, ResponseEnvelope } from '../../shared/protocol/envelope';

export type TransportKind = 'in-process' | 'http' | 'websocket';
export type TransportStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** Default time a transport waits for the reply to one request (SendOptions.timeoutMs). */
export const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;

export interface SendOptions {
  timeoutMs?: number;
}

export interface Transport {
  readonly kind: TransportKind;
  /**
   * True when authentication is bound to the connection (WebSocket, in-process): after a reconnect the client
   * must call `auth.resume`. HTTP authenticates every request with the bearer token instead.
   */
  readonly connectionBound: boolean;
  /** Sends one request; resolves with the response envelope, rejects with TransportError on transport failure. */
  send(request: RequestEnvelope, opts?: SendOptions): Promise<ResponseEnvelope>;
  onEvent(listener: (event: EventEnvelope) => void): () => void;
  onStatus(listener: (status: TransportStatus) => void): () => void;
  /** Bearer token for per-request authentication (HTTP); connection-bound transports ignore it. */
  setAuthToken(token: string | null): void;
  /**
   * Connection-bound transports that reconnect: `handler` runs on every re-established connection before
   * queued requests are released; it gets a `send` that bypasses that gate (used for `auth.resume`).
   */
  setReconnectHandler?(handler: ((send: (request: RequestEnvelope) => Promise<ResponseEnvelope>) => Promise<void>) | null): void;
  /**
   * Transports that resume the event stream themselves (HTTP long polling: events.poll after the last seq) report
   * here when the server could not replay what was missed (`gap`); events continue after `last`.
   */
  onGap?(listener: (gap: { after: number | null; last: number }) => void): () => void;
  /** Such transports: forget the event position (another player signed in; seqs are per player). */
  resetEventPosition?(): void;
  /**
   * Connection-bound transports: the server closed the connection for good with one of its close codes
   * (WS_CLOSE_CODES in src/shared/protocol/model.ts). After `sessionReplaced` (4004: the account signed in somewhere
   * else) the transport does NOT reconnect by itself (neither after a backoff nor on wake); the next request opens a
   * new connection.
   */
  onServerClose?(listener: (close: { code: number; reason: string }) => void): () => void;
  /**
   * Reconnecting transports: the app returned to the foreground (a phone's pause/resume, a laptop waking up). A lost
   * connection is re-established now instead of after the rest of its backoff wait.
   */
  wake?(): void;
  /**
   * Reconnecting transports: drops the current connection (one that no longer answers: a socket the OS or the network
   * killed silently while the app slept) and reconnects; the reconnect handler re-authenticates the new one.
   */
  restart?(): void;
  connect(): Promise<void>;
  close(): Promise<void>;
}

export class TransportError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'TransportError';
  }
}

/** Small listener registry used by the transports. */
export class Emitter<T> {
  private readonly listeners = new Set<(v: T) => void>();
  on(l: (v: T) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
  emit(v: T): void {
    for (const l of [...this.listeners]) {
      try {
        l(v);
      } catch {
        /* listener errors are isolated */
      }
    }
  }
  get size(): number {
    return this.listeners.size;
  }
}
